import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { SaveAgentFeedbackAdditionalLossPayload } from "../components/agent-feedback/default-additional-loss-form";

type SaveAgentFeedbackValidationPayload = Parameters<typeof adminAPI.saveAgentFeedbackClaimValidation>[1];

/** Port of services/api/download-file-from-response.ts (Vue) — request must use responseType: "blob". */
function downloadFileFromResponse(response: AxiosResponse<Blob>) {
  const contentDisposition = response.headers["content-disposition"] as string | undefined;
  const filename =
    contentDisposition
      ?.split("; ")
      .find((param) => param.startsWith("filename="))
      ?.split("=")[1] ?? "Document.pdf";

  const blob = new Blob([response.data], {
    type: (response.headers["content-type"] as string | undefined) || "application/pdf",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", decodeURIComponent(filename));
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Mirrors FormEligibilityVerification.submitForm (Vue) — saves the claim confirmation, and when the
 * agent reported no additional loss on a B2C travel, also submits the default additional-loss form.
 */
export function useSaveAgentFeedbackValidation(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      defaultAdditionalLoss,
    }: {
      payload: SaveAgentFeedbackValidationPayload;
      defaultAdditionalLoss?: SaveAgentFeedbackAdditionalLossPayload;
    }) =>
      notify.promise(
        (async () => {
          const result = await adminAPI.saveAgentFeedbackClaimValidation(claimId, payload);
          if (result.success && defaultAdditionalLoss) {
            await adminAPI.saveAgentFeedbackAdditionalLoss(claimId, defaultAdditionalLoss);
          }
          return result;
        })(),
        { loading: "Enregistrement du feedback…", success: "Éléments enregistrés avec succès" },
      ),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors FormAdditionalLoss.submitForm (Vue). */
export function useSaveAgentFeedbackAdditionalLoss(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveAgentFeedbackAdditionalLossPayload) =>
      notify.promise(adminAPI.saveAgentFeedbackAdditionalLoss(claimId, payload), {
        loading: "Enregistrement des éléments…",
        success: "Éléments enregistrés avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors FormAdditionalLoss.closeClaim (Vue) — Abandon event + marks the form step as completed. */
export function useCloseClaimFromAgentFeedback(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SaveAgentFeedbackAdditionalLossPayload) =>
      notify.promise(
        (async () => {
          await adminAPI.addEvent(claimId, { machineEvent: "Abandon" });
          // So that we know this form step is completed
          await adminAPI.saveAgentFeedbackAdditionalLoss(claimId, payload);
        })(),
        {
          loading: "Clôture du dossier…",
          success: () => ({
            message: "Dossier abandonné avec succès",
            description: "Vous pouvez fermer cette page ou naviguer sur un autre dossier",
          }),
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors setRequestDate (FormAdditionalLoss / FormUploadDocuments, Vue). */
export function useSetAdditionalLossRequestDate(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      notify.promise(adminAPI.setAdditionalLossRequestDate(claimId), {
        loading: "Enregistrement…",
        success: "Information enregistrée",
        error: "Une erreur est survenue, merci de nous contacter",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors FormUploadDocuments.downloadReceivables (Vue) — generates then downloads the receivables. */
export function useDownloadReceivables(claimId: string) {
  return useMutation({
    mutationFn: () =>
      notify.promise(
        (async () => {
          const response = await adminAPI.instance.post<Blob>(
            `/admin/claims/${claimId}/documents/receivable-contract`,
            null,
            { responseType: "blob" },
          );
          downloadFileFromResponse(response);
        })(),
        {
          loading: "Document en cours de génération (~30 sec à 1min)...",
          success: "Cessions générées avec succès",
        },
      ),
  });
}

/** Public Box folder holding the generated receivables — mirrors BoxLinks (Vue). */
export function useReceivablesBoxFolder(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "receivables-box-folder"],
    queryFn: () => adminAPI.getReceivablesBoxFolder(claimId),
  });
}

/** Public upload link to transmit signed documents — mirrors BoxLinks (Vue). */
export function useFileUploadUrl(claimId: string) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "file-upload-url"],
    queryFn: () => adminAPI.getFileUploadUrl(claimId),
  });
}

/** Business travel companies of the claim's agency — mirrors SetBusinessTravelCompany (Vue). */
export function useBusinessTravelCompanies(claimId: string, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "business-travel-companies"],
    queryFn: async () => {
      const { travelAgencyCompanies } = await adminAPI.listBusinessTravelCompaniesForClaim(claimId);
      return travelAgencyCompanies;
    },
    enabled,
  });
}

/** Mirrors SetBusinessTravelCompany.selectEnterprise (Vue). */
export function useSetTravelAgencyCompanyForClaim(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { travelAgencyCompanyId: string } | { unset: true }) =>
      notify.promise(adminAPI.setTravelAgencyCompanyForClaim(claimId, payload), {
        loading: "Enregistrement de l'entreprise…",
        success: "Entreprise enregistrée",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/** Mirrors SetBusinessTravelCompany.createEnterprise (Vue). */
export function useCreateTravelAgencyCompanyFromClaim(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof adminAPI.createTravelAgencyCompanyFromClaim>[1]) =>
      notify.promise(adminAPI.createTravelAgencyCompanyFromClaim(claimId, payload), {
        loading: "Création de l'entreprise…",
        success: "Entreprise ajoutée avec succès",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}
